import { SortDirection } from '@bp/bp-graphql-types';
import { graphql } from 'client/gql';
import { AbsenceForm, UpdateAbsenceMutationDocument } from 'components/Absences/forms/AbsenceForm';
import { AbsenceModal } from 'components/Absences/modals/AbsenceModal';
import { MyAbsencesTable } from 'components/Absences/tables/MyAbsencesTable';
import { MyAbsenceContext } from 'components/Absences/types/absenceTypes';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';

const DeleteAbsenceMutationDocument = graphql(`
  mutation BpDeleteAbsence($where: AbsenceWhere!) {
    deleteAbsences(where: $where) {
      nodesDeleted
    }
  }
`);

const MyAbsencesSubpagePageDocument = graphql(`
  query MyAbsencesSubpagePageData($absencesWhere: AbsenceWhere!) {
    ...MyAbsencesTableFields
  }
`);

export const MyAbsencesSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profile = pimAuthClaims.getProfile();
  const children = pimAuthClaims.getChildProfiles();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [selectedAbsenceUuid, setSelectedAbsenceUuid] = useState<string | null>(null);
  const [absencesContext, setAbsenceContext] = useState<MyAbsenceContext>('child');
  const [absencesModalOpen, setAbsencesModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [, updateAbsence] = useMutation(UpdateAbsenceMutationDocument);
  const [, deleteAbsence] = useMutation(DeleteAbsenceMutationDocument);

  const [{ data }] = useQuery({
    query: MyAbsencesSubpagePageDocument,
    variables: {
      absencesWhere: {
        owner: { uuid_IN: [profile.uuid, ...children.map((c) => c.uuid)] },
        startTime_GTE: currentSchoolYear.start,
        endTime_LTE: currentSchoolYear.end,
      },
      options: { sort: [{ startTime: SortDirection.Desc }] },
    },
  });

  const permissions = usePermissionChecker();

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('absences.title', { count: 1 }),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  function handleAdd(context: MyAbsenceContext) {
    setAbsenceContext(context);
    setAbsencesModalOpen(true);
  }

  function onEdit(uuid: string, context: MyAbsenceContext) {
    setSelectedAbsenceUuid(uuid);
    setAbsenceContext(context);
    setAbsencesModalOpen(true);
  }

  async function handleExcuse(uuid: string, isExcused: boolean) {
    setLoading(true);

    const { error } = await updateAbsence({
      where: {
        uuid,
      },
      update: {
        excused: isExcused,
      },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    setLoading(false);
  }

  async function handleDelete(uuid: string) {
    const res = await confirmDelete();
    if (!res) return;

    setLoading(true);

    const { error } = await deleteAbsence({ where: { uuid } });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }

    setLoading(false);
    setSelectedAbsenceUuid(null);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canViewOwnAbsences()}>
      <MyAbsencesTable
        isLoading={loading}
        data={data}
        onAdd={(context) => handleAdd(context)}
        onEdit={(uuid, context) => onEdit(uuid, context)}
        onExcuse={(uuid, isExcused) => handleExcuse(uuid, isExcused)}
        onDelete={(uuid) => handleDelete(uuid)}
      />

      <AbsenceModal
        isOpen={absencesModalOpen}
        context={absencesContext}
        selectedAbsenceUuid={selectedAbsenceUuid}
        onClose={() => {
          setAbsencesModalOpen(false);
          setSelectedAbsenceUuid(null);
        }}
      />

      <ConfirmationDialog />
    </BpSubpage>
  );
};
